import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTimes, FaReceipt } from 'react-icons/fa';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Button from '../../components/shared/Button';
import TextArea from '../../components/shared/TextArea';
import { createBusinessExpense, updateBusinessExpense } from './financialService';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`;

const ModalTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${props => props.theme.colors.inputGray}; color: ${props => props.theme.colors.textPrimary}; }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.cols || '1fr'};
  gap: 16px;
  margin-bottom: 4px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${props => props.theme.colors.inputGray};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelBtn = styled.button`
  padding: 12px 20px;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.inputGray};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${props => props.theme.colors.background}; }
`;

const CATEGORIES = [
  'Advertisement', 'Insurance', 'Marketing', 'Licensing & Fees', 'Office Supplies',
  'Vehicle', 'Vehicle Fuel', 'Equipment Fuel', 'Maintenance', 'COGS', 'General Labor / Helper',
  'Software & Subscriptions', 'Rent & Utilities', 'Professional Services', 'Other'
];

const todayLocalISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toDateInputValue = (value) => {
  if (!value) return todayLocalISO();
  if (typeof value === 'string') return value.slice(0, 10);
  return todayLocalISO();
};

const BusinessExpenseForm = ({ expense, onClose, onSuccess }) => {
  const isEdit = !!expense;

  const [formData, setFormData] = useState({
    category: expense?.category || 'Other',
    description: expense?.description || '',
    amount: expense?.amount || '',
    expenseDate: toDateInputValue(expense?.expenseDate),
    vendor: expense?.vendor || '',
    referenceNumber: expense?.referenceNumber || '',
    notes: expense?.notes || ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.category || !formData.description || !formData.amount || !formData.expenseDate) {
      setError('Category, description, amount, and date are required');
      return;
    }

    try {
      setSubmitting(true);
      if (isEdit) {
        await updateBusinessExpense(expense.id, { ...formData, amount: parseFloat(formData.amount) });
      } else {
        await createBusinessExpense({ ...formData, amount: parseFloat(formData.amount) });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalTop>
          <ModalTitle><FaReceipt /> {isEdit ? 'Edit Expense' : 'New Expense'}</ModalTitle>
          <CloseBtn onClick={onClose}><FaTimes /></CloseBtn>
        </ModalTop>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <ErrorBanner>{error}</ErrorBanner>}

            <FormRow cols="1fr 1fr">
              <div>
                <Label>Category</Label>
                <Select
                  label="Category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </FormRow>

            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="What was this expense for?"
                required
              />
            </div>

            <FormRow cols="1fr 1fr">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.expenseDate}
                  onChange={(e) => handleChange('expenseDate', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Vendor</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                  placeholder="e.g. State Farm"
                />
              </div>
            </FormRow>

            <div>
              <Label>Reference #</Label>
              <Input
                value={formData.referenceNumber}
                onChange={(e) => handleChange('referenceNumber', e.target.value)}
                placeholder="Invoice #, policy #, etc."
              />
            </div>

            <div>
              <Label>Notes</Label>
              <TextArea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional notes..."
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : (isEdit ? 'Update Expense' : 'Add Expense')}
            </Button>
          </ModalFooter>
        </form>
      </ModalCard>
    </Overlay>
  );
};

export default BusinessExpenseForm;
